import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Play } from "lucide-react";

const slides = [
  {
    img: "/images/washplant.jpg",
    tag: "WASH PLANT FLOOR",
    title: "Where raw feed becomes concentrate.",
    body: "Hands-on at the plant — our team works the wash circuit that upgrades raw chrome feed to a consistent 40–42% grade, and higher when the market demands it.",
    testid: "onsite-slide-1",
  },
  {
    img: "/images/inspect.jpg",
    tag: "FINAL CONCENTRATE",
    title: "The finished grade, verified together.",
    body: "Our washed chrome concentrate — 40–42% and higher grades — inspected pile by pile with our partners before it enters the logistics corridor.",
    testid: "onsite-slide-2",
  },
  {
    img: "/images/stockpile.jpg",
    tag: "RAW FEED",
    title: "Raw material, bound for the wash plant.",
    body: "Run-of-mine chrome aggregated from across the local corridor — this raw feed enters our wash circuit and comes out as graded concentrate.",
    testid: "onsite-slide-3",
  },
];

const VideoSlide = ({ src, testid, onPlayingChange }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
      onPlayingChange(true);
    } else {
      v.pause();
      setPlaying(false);
      onPlayingChange(false);
    }
  };

  return (
    <div className="relative w-full h-full">
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        playsInline
        preload="metadata"
        onEnded={() => { setPlaying(false); onPlayingChange(false); }}
      />
      {!playing ? (
        <button
          data-testid={`${testid}-play-button`}
          onClick={toggle}
          aria-label="Play video"
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors duration-300"
        >
          <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform duration-300">
            <Play size={24} className="text-ink ml-1" fill="currentColor" />
          </span>
        </button>
      ) : (
        <button data-testid={`${testid}-pause-button`} onClick={toggle} aria-label="Pause video" className="absolute inset-0 z-10" />
      )}
    </div>
  );
};

export const OnSite = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setIndex((i) => (i + 1) % slides.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + slides.length) % slides.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next, paused]);

  const slide = slides[index];

  return (
    <section id="on-site" data-testid="onsite-section" className="bg-surface px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-chrome mb-6">From the Ground</p>
          <h2 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl md:text-6xl leading-[0.95] text-ink" data-testid="onsite-heading">
            WHEN WE'RE<br />ON SITE.
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500 mr-4" data-testid="onsite-counter">
            {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
          <button
            data-testid="onsite-prev-button"
            onClick={prev}
            aria-label="Previous slide"
            className="w-12 h-12 border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors duration-300"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            data-testid="onsite-next-button"
            onClick={next}
            aria-label="Next slide"
            className="w-12 h-12 border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors duration-300"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="absolute -top-4 -left-4 w-full h-full border border-chrome/25 pointer-events-none z-0" />
        <div className="relative overflow-hidden bg-ink aspect-[4/3] md:aspect-[16/9] max-h-[80vh]">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              data-testid={slide.testid}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              {slide.type === "video" ? (
                <VideoSlide src={slide.img} testid={slide.testid} onPlayingChange={setPaused} />
              ) : (
                <img src={slide.img} alt={slide.title} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-2xl">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80 mb-3">
                  <MapPin size={12} />
                  <span data-testid={`${slide.testid}-tag`}>{slide.tag}</span>
                </div>
                <h3 className="font-display font-semibold text-2xl md:text-4xl tracking-tight text-white leading-tight" data-testid={`${slide.testid}-title`}>
                  {slide.title}
                </h3>
                <p className="text-sm md:text-base text-white/80 mt-3 leading-relaxed" data-testid={`${slide.testid}-body`}>
                  {slide.body}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex gap-2 mt-6">
          {slides.map((_, i) => (
            <button
              key={i}
              data-testid={`onsite-dot-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1 transition-[width,background-color] duration-500 ${i === index ? "w-12 bg-chrome" : "w-6 bg-ink/20 hover:bg-ink/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
